import { HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { BillingStatusDto } from './api/models';
import {
  AuthService,
  BillingService,
  CustomEliteConfigurationService,
  HealthService,
  MatchVideoService,
  PreanalysisService,
  SuperadminService,
  UserService,
  VisionEventsService,
} from './api/services';
import { CUSTOM_REQUEST_AUTHORIZATION, CUSTOM_REQUEST_TIMEOUT_TOKEN } from './auth/auth.interceptor';
import { CoreProvider } from './core.provider';

@Injectable({
  providedIn: 'root',
})
export class ApiProvider {
  private connectedToCloudInterval: any;
  private connectedToCloudContext = new HttpContext();
  connectedToCloud$ = new BehaviorSubject<boolean>(false);

  private checkSubscriptionStatusInterval: any;
  private checkSubscriptionStatusIntervalStartTime?: number;
  /**
   * Seeded one below the real free allowance on purpose: if the status check
   * fails we would rather under-promise than let someone upload 6 GB and only
   * then be told they cannot analyse it.
   */
  billingStatus$ = new BehaviorSubject<BillingStatusDto>({ isActive: false, status: 'inactive', freeVideosRemaining: 2 });

  get isUpgradeRequired() {
    return !['active', 'free'].includes(this.billingStatus$.value.status) && this.billingStatus$.value.freeVideosRemaining <= 0;
  }

  private core!: CoreProvider;
  setCore(core: CoreProvider) {
    this.core = core;
    void this.core.storage.getItem('last-billing-status').then((last) => {
      if (last) this.billingStatus$.next(JSON.parse(last) as BillingStatusDto);
    });
  }

  constructor(
    public readonly health: HealthService,
    public readonly auth: AuthService,
    public readonly user: UserService,
    public readonly billing: BillingService,
    public readonly matchVideo: MatchVideoService,
    public readonly preanalysis: PreanalysisService,
    public readonly visionEvents: VisionEventsService,
    public readonly superadmin: SuperadminService,
    public readonly customEliteConfigurationService: CustomEliteConfigurationService,
  ) {
    this.connectedToCloudContext.set(CUSTOM_REQUEST_AUTHORIZATION, 'no-auth-required'); // Prevent waiting for tokens initialization
    this.connectedToCloudContext.set(CUSTOM_REQUEST_TIMEOUT_TOKEN, 2000);

    const checkConnection = async () => {
      try {
        await firstValueFrom(this.health.healthControllerPing({}, this.connectedToCloudContext));
        if (this.connectedToCloud$.value !== true) {
          this.connectedToCloud$.next(true);
          if (this.core?.auth.isAuthenticated) void this.checkSubscriptionStatus();
        }
      } catch {
        if (this.connectedToCloud$.value !== false) this.connectedToCloud$.next(false);
      }
    };
    if (this.connectedToCloudInterval) clearInterval(this.connectedToCloudInterval);
    this.connectedToCloudInterval = setInterval(() => void checkConnection(), 4000);
    void checkConnection();
  }

  async checkSubscriptionStatus(): Promise<BillingStatusDto> {
    try {
      const status = (await firstValueFrom(this.billing.billingControllerGetBillingStatus({}))) as BillingStatusDto;
      void this.core.storage.setItem('last-billing-status', JSON.stringify(status));
      this.billingStatus$.next(status);
      if (status.isActive && this.checkSubscriptionStatusInterval) {
        clearInterval(this.checkSubscriptionStatusInterval);
        this.checkSubscriptionStatusInterval = undefined;
      }
      return status;
    } catch (error) {
      console.error('Error checking subscription status', error);
      throw error;
    }
  }

  async afterPurchaseStartCheckInterval(): Promise<void> {
    this.checkSubscriptionStatusIntervalStartTime = Date.now();
    this.checkSubscriptionStatusInterval = setInterval(() => {
      void this.checkSubscriptionStatus();
      if (
        this.checkSubscriptionStatusInterval &&
        this.checkSubscriptionStatusIntervalStartTime &&
        Date.now() - this.checkSubscriptionStatusIntervalStartTime > 15 * 60 * 1000 // 15 minutes to avoid an infinite loop
      ) {
        clearInterval(this.checkSubscriptionStatusInterval);
        this.checkSubscriptionStatusInterval = undefined;
      }
    }, 30000);
  }
}
